import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export const CalendarOAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing calendar connection...');
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const calendarParam = urlParams.get('calendar');
        const calendarEmail = urlParams.get('email');
        const errorParam = urlParams.get('error');

        if (calendarParam === 'connected') {
          setStatus('success');
          setMessage('Google Calendar connected successfully!');
          setEmail(calendarEmail);

          if (window.opener) {
            window.opener.postMessage(
              { type: 'CALENDAR_OAUTH_SUCCESS', email: calendarEmail },
              window.location.origin,
            );
          }
          setTimeout(() => window.close(), 2000);
        } else if (calendarParam === 'error') {
          setStatus('error');
          // URLSearchParams.get() already decodes the value — do not call decodeURIComponent again.
          const errorMessage = errorParam || 'Failed to connect Google Calendar';
          setMessage(errorMessage);

          if (window.opener) {
            window.opener.postMessage(
              { type: 'CALENDAR_OAUTH_ERROR', error: errorMessage },
              window.location.origin,
            );
          }
          setTimeout(() => window.close(), 3000);
        } else {
          setMessage('Completing calendar connection...');
          setTimeout(() => {
            setStatus('error');
            setMessage('Connection timed out. Please try again.');
            if (window.opener) {
              window.opener.postMessage(
                { type: 'CALENDAR_OAUTH_ERROR', error: 'Connection timed out' },
                window.location.origin,
              );
            }
            setTimeout(() => window.close(), 2000);
          }, 30000);
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred');
        if (window.opener) {
          window.opener.postMessage(
            { type: 'CALENDAR_OAUTH_ERROR', error: 'Unexpected error' },
            window.location.origin,
          );
        }
        setTimeout(() => window.close(), 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          {status === 'loading' && (
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          )}
          {status === 'error' && (
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          )}
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          {status === 'loading' && 'Connecting Calendar...'}
          {status === 'success' && 'Success!'}
          {status === 'error' && 'Connection Failed'}
        </h2>
        <p className="text-slate-600 mb-4">{message}</p>
        {email && (
          <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
            Connected: {email}
          </p>
        )}
        {status !== 'loading' && (
          <p className="text-xs text-slate-400 mt-4">This window will close automatically...</p>
        )}
        <button
          id="calendar-oauth-close-btn"
          onClick={() => window.close()}
          className="mt-4 px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
        >
          Close Window
        </button>
      </div>
    </div>
  );
};
